/// <amd-module name="VSSUI/Utilities/EditableDropdownItemProvider" />
import {
    IObservable,
    IObservableArrayEventArgs,
    IReadonlyObservableArray,
    ObservableArray,
    ObservableArrayAction,
    ObservableCollection,
    ObservableLike
} from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem, wrapListBoxItems } from "VSSUI/ListBox";
import * as Resources from "VSSUI/Resources.Dropdown";
import { IItemProvider } from "VSSUI/Utilities/Provider";

export class EditableDropdownItemProvider<T = {}>
    implements IItemProvider<IListBoxItem<T>>, IObservable<IObservableArrayEventArgs<IListBoxItem<T>>, ObservableArrayAction> {
    private itemCollection: ObservableCollection<IListBoxItem<T>>;
    private listItems: IReadonlyObservableArray<IListBoxItem<T>>;
    private extraItem: ObservableArray<IListBoxItem<T>>;
    private selection: IListSelection;

    /**
     * Create a Provider that manages focus on rows with a focused class.
     * @param listItems the current set of filtered items.
     * @param selection The selection object associstaed with items.
     */
    public constructor(listItems: IReadonlyObservableArray<IListBoxItem<T>> | IListBoxItem<T>[] | string[], selection: IListSelection) {
        this.itemCollection = new ObservableCollection<IListBoxItem<T>>();
        if (ObservableLike.isObservable(listItems)) {
            this.listItems = listItems as IReadonlyObservableArray<IListBoxItem<T>>;
        } else {
            this.listItems = new ObservableArray<IListBoxItem<T>>(wrapListBoxItems(listItems) || (listItems as IListBoxItem<T>[]));
        }
        this.itemCollection.push(this.listItems);
        this.extraItem = new ObservableArray<IListBoxItem<T>>();
        this.itemCollection.push(this.extraItem);
        this.selection = selection;
    }

    /**
     * Get the length of the listItems array.
     */
    public get length(): number {
        return this.itemCollection.length;
    }

    /**
     * Get the interal array of listItems.
     */
    public get value(): IListBoxItem<T>[] {
        return this.itemCollection.value;
    }

    /**
     * Return whether or not there is currently an extra item added for a freeform EditableDropdown.
     */
    public get hasExtraItem(): boolean {
        return this.extraItem.length > 0;
    }

    /**
     * Subscribe to changes in the underlying set of items.
     * @param observer the delegate to be called when there are updates.
     * @param action the action on the set to observe.
     */
    public subscribe(
        observer: (value: IObservableArrayEventArgs<IListBoxItem<T>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): (value: IObservableArrayEventArgs<IListBoxItem<T>>, action?: ObservableArrayAction) => void {
        return this.itemCollection.subscribe(observer, action);
    }

    /**
     * Unsubscribe from changes in the underlying set of items.
     * @param observer the delegate that was used to subscribe.
     * @param action the action that was used to subsribe.
     */
    public unsubscribe(
        observer: (value: IObservableArrayEventArgs<IListBoxItem<T>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): void {
        return this.itemCollection.unsubscribe(observer, action);
    }

    /**
     * Set the text value currently typed in the EditableDropdown to see if an extra item needs to be added
     * to represent that value.  Only call this if allowFreeform is true.
     * @param value
     */
    public setTextValue(value: string) {
        if (this.hasExtraItem) {
            this.extraItem.splice(0, 1);
        }
        if (value) {
            const index = this.listItems.value.findIndex(item => item.text === value);
            if (index === -1 || !this.selection.selectable(index)) {
                this.extraItem.push({ id: value, text: format(Resources.UseItem, value), className: editableDropdownExtraItemClassName });
            }
        }
    }
}

export const editableDropdownExtraItemClassName = "editable-dropdown-extra-item";
